/**
 * documents-api.ts — typed feature wrapper + pure display helpers for the DMS
 * Ablage grid + inline PDF viewer (dms/spec.md §M11).
 *
 * The M6/M7 backend already resolves every DISPLAY field server-side
 * (`category`, `state`, `bezug`, `quelle`, `doc_date`) — `DocumentListItemDto`
 * needs NO client-side label mapping (coding-standards §3: components render
 * server verdicts, never re-derive domain rules). This module is limited to:
 *   - byte-size formatting (KB/MB — presentational only, not a domain rule);
 *   - the Kategorie/Quelle CLIENT-SIDE filter-option derivation (the grid
 *     filters over the currently-loaded window by the resolved display
 *     strings — see DocumentsScreen for why this is separate from the
 *     server-side `q` fulltext leg, which matches filename/extracted_text
 *     only, NOT `bezug`/`quelle`/`category`);
 *   - the `title ?? filename` fallback for `DocumentDetailDto` (the ONE DTO
 *     that does NOT pre-resolve title — dms/spec.md/document.ts).
 *
 * [Source: apps/web/src/features/policies/api/policies-api.ts] (wrapper-module precedent)
 * [Source: packages/shared/src/document.ts] (DTO contract)
 */

import { apiClient } from '../../../lib/api-client';
import type { DocumentListQuery } from '../../../lib/api-client';
import type {
  DocumentDedupMethod,
  DocumentDetailDto,
  DocumentListItemDto,
  DuplicateInfoDto,
} from '@brokly/shared';

export type { DocumentListQuery };

// ── Fetch wrappers ───────────────────────────────────────────────────────────

/** GET /documents (paginated mode) → rows + total count for the "N von M" toolbar. */
export async function fetchDocumentList(
  params: DocumentListQuery = {},
): Promise<{ items: DocumentListItemDto[]; totalCount: number | null }> {
  const res = await apiClient.documents.list(params);
  return { items: res.items, totalCount: res.page.total_count ?? null };
}

/** GET /documents/:id → the viewer's detail + connected_objects. */
export function fetchDocumentDetail(id: string): Promise<DocumentDetailDto> {
  return apiClient.documents.get(id);
}

/**
 * GET /documents/duplicates → the admin "Duplikate" view's method/score/detected_at
 * (P2-12, dms/spec-part2-stefan-delta.md §2.4). Admin-gated server-side — the caller
 * (`useDuplicateRows`) only fires this when `useEffectivePermissions().isWildcard`.
 */
export function fetchDuplicates(): Promise<DuplicateInfoDto[]> {
  return apiClient.documents.duplicates();
}

// ── Display helpers (pure, presentational only) ─────────────────────────────

/** `≥10` → whole units; `<10` → 1 decimal, but never a trailing ".0" (e.g. 2 KB, not "2.0 KB"). */
function formatSizeUnit(value: number): string {
  if (value >= 10) return String(Math.round(value));
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

/** `size_bytes` → human KB/MB (dms/spec.md §4.1 "Grösse" column). `null` → "—". */
export function sizeDisplay(bytes: number | null): string {
  if (bytes === null || !Number.isFinite(bytes)) return '-';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${formatSizeUnit(kb)} KB`;
  const mb = kb / 1024;
  return `${formatSizeUnit(mb)} MB`;
}

/**
 * `DocumentDetailDto.title` is the ONE DMS field that is NOT server-resolved
 * (unlike `DocumentListItemDto.title`, already `title ?? filename`) — mirrors
 * the api's own `resolveDocumentTitle` (documents-display.util.ts).
 */
export function resolveTitle(title: string | null, filename: string): string {
  return title ?? filename;
}

/** The base Quelle label before the "· source_label" suffix, e.g. "Posteingang · Swica" → "Posteingang". */
export function quelleBase(quelle: string): string {
  return quelle.split(' · ')[0] ?? quelle;
}

/** Distinct, sorted Kategorie values present in the current row window (client-side filter options). */
export function categoryOptions(rows: readonly DocumentListItemDto[]): string[] {
  return Array.from(
    new Set(rows.map((r) => r.category).filter((c): c is string => c !== null)),
  ).sort();
}

/** Distinct, sorted Quelle BASE values present in the current row window (client-side filter options). */
export function quelleOptions(rows: readonly DocumentListItemDto[]): string[] {
  return Array.from(new Set(rows.map((r) => quelleBase(r.quelle)))).sort();
}

// ── Duplikate view display helpers (P2-12) ──────────────────────────────────

/** `DuplicateInfoDto.method` → the German label for the admin "Duplikate" view's Methode column. */
const DEDUP_METHOD_LABELS: Record<DocumentDedupMethod, string> = {
  file_hash: 'Datei-Hash',
  content_key: 'Inhalts-Schlüssel',
  embedding: 'Ähnlichkeit (KI)',
};

/** `null`-safe label for `DuplicateInfoDto.method` — falls back to the raw value for a future enum member. */
export function methodLabel(method: DocumentDedupMethod): string {
  return DEDUP_METHOD_LABELS[method] ?? method;
}

/** `DuplicateInfoDto.score` (numeric(4,3) string, Tier-3/embedding only) → display string. `null` → "—". */
export function scoreDisplay(score: string | null): string {
  if (score === null) return '-';
  const n = Number(score);
  return Number.isFinite(n) ? `${Math.round(n * 100)}%` : score;
}
